// ===========================================
// Error Boundary - Catches React Errors
// ===========================================
// Prevents the entire app from crashing when a component throws an error
// Shows a fallback UI and allows users to recover

import React, { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  // Update state when an error is caught
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  // Log error details for debugging
  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // In production, you could send this to an error tracking service
    // e.g., Sentry, LogRocket, etc.
  }

  // Reset the error state and attempt to recover
  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  // Navigate to home page
  handleGoHome = () => {
    this.handleReset();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI when an error occurs
      return (
        <div style={styles.container}>
          <div style={styles.content}>
            {/* Error icon */}
            <div style={styles.iconContainer}>
              <svg 
                width="64" 
                height="64" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={styles.icon}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            
            {/* Error message */}
            <h1 style={styles.title}>Something went wrong</h1>
            <p style={styles.message}>
              We're sorry, but something unexpected happened. 
              Please try again or return to the home page.
            </p>
            
            {/* Error details (development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error Details</summary>
                <pre style={styles.errorStack}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            {/* Action buttons */}
            <div style={styles.buttonContainer}>
              <button onClick={this.handleReset} style={styles.button}>
                Try Again
              </button>
              <button onClick={this.handleGoHome} style={styles.buttonSecondary}>
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Render children when there's no error
    return this.props.children;
  }
}

// Inline styles for the error boundary UI
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0f1419',
    padding: '2rem',
  },
  content: {
    textAlign: 'center',
    maxWidth: '500px',
  },
  iconContainer: {
    marginBottom: '1.5rem',
  },
  icon: {
    color: '#ef4444',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#f3f4f6',
    marginBottom: '0.75rem',
  },
  message: {
    color: '#9ca3af',
    marginBottom: '1.5rem',
    lineHeight: '1.6',
  },
  details: {
    marginBottom: '1.5rem',
    textAlign: 'left',
    backgroundColor: '#1a1f26',
    borderRadius: '8px',
    padding: '1rem',
    border: '1px solid #374151',
  },
  summary: {
    cursor: 'pointer',
    color: '#f59e0b',
    fontWeight: '500',
    marginBottom: '0.5rem',
  },
  errorStack: {
    color: '#ef4444',
    fontSize: '0.75rem',
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: '0.5rem',
  },
  buttonContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
  button: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#f59e0b',
    color: '#0f1419',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonSecondary: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'transparent',
    color: '#9ca3af',
    border: '1px solid #374151',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
};

export default ErrorBoundary;

